import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { pct } from '@/lib/format';
import { ArrowDown, ArrowUp, Info } from './icons';
import { Tooltip } from './overlay';

/* ────────────────────────────────────────────────────────────────────────────
   Stats.

   Every stat is an uppercase eyebrow over a monospaced figure. Numbers are
   tabular so a ticking value never nudges the layout.
   ──────────────────────────────────────────────────────────────────────────── */

/** Below this, a change is noise — render an en dash instead of a fake sign. */
const FLAT = 0.00005;

export interface DeltaProps {
  /** Fraction, not percent: 0.0421 → "+4.21%". */
  value: number;
  showArrow?: boolean;
  digits?: number;
  className?: string;
}

/**
 * Signed change. Colour is never the only signal — every state also carries a
 * glyph (arrow up / arrow down / en dash) and an explicit sign.
 */
export function Delta({ value, showArrow = true, digits = 2, className }: DeltaProps) {
  const finite = Number.isFinite(value);
  const flat = !finite || Math.abs(value) < FLAT;
  const up = finite && value > 0;

  const tone = flat ? 'text-muted' : up ? 'text-up' : 'text-down';
  const label = !finite ? '—' : flat ? pct(0, { digits, sign: false }) : pct(value, { digits });

  return (
    <span
      className={cn('inline-flex items-center gap-0.5 font-mono text-xs font-medium', tone, className)}
    >
      {showArrow ? (
        flat ? (
          <span aria-hidden="true" className="px-px leading-none">
            –
          </span>
        ) : up ? (
          <ArrowUp size={12} className="shrink-0" />
        ) : (
          <ArrowDown size={12} className="shrink-0" />
        )
      ) : null}
      {label}
    </span>
  );
}

export type StatSize = 'sm' | 'md' | 'lg';
export type StatTone = 'default' | 'bull' | 'floor' | 'up' | 'down';

export interface StatProps {
  label: string;
  value: ReactNode;
  /** Fraction change, rendered through `<Delta />`. */
  delta?: number;
  sub?: ReactNode;
  size?: StatSize;
  tone?: StatTone;
  icon?: ReactNode;
  align?: 'left' | 'right';
  /** Adds an info affordance with this text in a tooltip. */
  hint?: string;
  className?: string;
}

const VALUE_SIZE: Record<StatSize, string> = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-2xl sm:text-[1.75rem]',
};

const TONE: Record<StatTone, string> = {
  default: 'text-fg',
  bull: 'text-bull',
  floor: 'text-floor',
  up: 'text-up',
  down: 'text-down',
};

export function Stat({
  label,
  value,
  delta,
  sub,
  size = 'md',
  tone = 'default',
  icon,
  align = 'left',
  hint,
  className,
}: StatProps) {
  const right = align === 'right';
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1',
        right ? 'items-end text-right' : 'items-start text-left',
        className,
      )}
    >
      <div className={cn('flex items-center gap-1.5', right && 'flex-row-reverse')}>
        {icon ? <span className="inline-flex shrink-0 text-subtle">{icon}</span> : null}
        <span className="text-3xs uppercase tracking-[0.14em] text-subtle">{label}</span>
        {hint ? (
          <Tooltip content={hint}>
            <button
              type="button"
              aria-label={`${label}: more information`}
              /* `-m-1.5 p-1.5` lifts a 12px icon to a 24px pointer target
                 (WCAG 2.5.8) without moving anything around it. */
              className="-m-1.5 inline-flex shrink-0 rounded p-1.5 text-subtle transition-colors duration-150 ease-swift hover:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70"
            >
              <Info size={12} />
            </button>
          </Tooltip>
        ) : null}
      </div>

      <div className={cn('flex items-baseline gap-2', right && 'flex-row-reverse')}>
        <span
          className={cn(
            'truncate font-mono font-semibold leading-none tabular-nums',
            VALUE_SIZE[size],
            TONE[tone],
          )}
        >
          {value}
        </span>
        {delta !== undefined ? <Delta value={delta} /> : null}
      </div>

      {sub ? <span className="truncate text-2xs leading-snug text-muted">{sub}</span> : null}
    </div>
  );
}
